import useAuthRedirect from '@/hooks/useAuthRedirect';
import usePermission from '@/hooks/usePermission';
import useOrdersPaused from '@/hooks/useOrdersPaused';
import { message, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { ROUTE } from '@/config';
import { useUserStore } from '@/stores';
import { getDefaultRoute } from '@/utils';

interface PrivateRouteProps {
  element: JSX.Element;
  requiredPermissions?: string[];
  blockedWhenOrdersPaused?: boolean;
}

const PrivateRoute = ({
  element,
  requiredPermissions,
  blockedWhenOrdersPaused,
}: PrivateRouteProps) => {
  useAuthRedirect();

  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const navigate = useNavigate();
  const user = useUserStore((state) => state.user);
  const ordersPaused = useOrdersPaused();

  // Wait for user data to load before checking permissions
  // Check if permissions are actually defined (not undefined)
  // undefined = still loading, [] or array = loaded
  const permissionsLoaded = user?.permissions !== undefined;

  // Calculate permissions (must be called on every render - Rules of Hooks)
  const hasRequiredPermissions =
    !requiredPermissions ||
    requiredPermissions.length === 0 ||
    hasPermission(...requiredPermissions);

  // Merchant Orders Pause — an intake route a paused merchant may not open.
  //
  // Gated on `permissionsLoaded` for the same reason the permission check is:
  // before GET /user resolves, `ordersPaused` is false because the field is
  // simply absent, so acting early would be acting on "not loaded yet", not on
  // "accepting". Below, the un-loaded case renders a spinner anyway.
  const blockedByPause = Boolean(blockedWhenOrdersPaused) && ordersPaused;

  // Effect to show error and redirect (must be called on every render - Rules of Hooks)
  useEffect(() => {
    // Only run this effect after permissions are loaded
    if (permissionsLoaded && !hasRequiredPermissions) {
      message.error(t('you_do_not_have_permission_to_view_this_page'));
      const defaultRoute = getDefaultRoute(hasPermission);
      navigate(defaultRoute, { replace: true });
      return;
    }

    if (permissionsLoaded && blockedByPause) {
      message.error(t('orders_paused_message'));
      navigate(getDefaultRoute(hasPermission), { replace: true });
    }
  }, [permissionsLoaded, hasRequiredPermissions, blockedByPause, navigate, t, hasPermission]);

  // NOW we can do conditional returns (after all hooks are called)
  // Show loading spinner while waiting for user data
  if (!user || !permissionsLoaded) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
        }}
      >
        <Spin size='large' />
      </div>
    );
  }

  // If no permissions, return null (redirect happens in useEffect above)
  if (!hasRequiredPermissions) {
    return null;
  }

  // Same contract for the pause: the page never mounts, so none of its queries
  // fire and the merchant never sees a half-drawn create form before the
  // redirect lands.
  if (blockedByPause) {
    return null;
  }

  return element;
};

export default PrivateRoute;
